import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, BedDouble, Stethoscope, ClipboardList, ChevronRight,
  Activity, Droplets, TriangleAlert, Save, Cake, Utensils, Pill, Search, UserPlus, CheckCircle2, RotateCcw, Monitor, Printer,
} from "lucide-react";
import { useApp, calcularIdade } from "../context/AppContext";
import { statusHidratacao, medicacaoNaHora, statusEvacuacao, statusDecubito } from "../utils/alertas";
import HydrationDonut from "../components/HydrationDonut";
import DecubitoAlarm from "../components/DecubitoAlarm";
import ResidenteFormDialog from "../components/ResidenteFormDialog";
import { AvisoAlertaTopo } from "../components/AvisoAlertaTopo";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { useToast } from "../hooks/use-toast";

const grauBadge = {
  "1": { label: "Grau I", cls: "text-emerald-700 bg-emerald-50" },
  "2": { label: "Grau II", cls: "text-amber-700 bg-amber-50" },
  "3": { label: "Grau III", cls: "text-red-700 bg-red-50" },
};
const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const ASPECTOS = ["normal", "diarreia", "constipação", "melena", "hematoquezia", "fecaloma"];
const agoraISO = () => {
  const d = new Date(); const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const TIPO_TITULO = {
  sinais_vitais: "Inserir Sinais Vitais", balanco_hidrico: "Registrar Hidratação",
  evacuacao: "Registrar Evacuação", ocorrencia: "Registrar Ocorrência", anotacao_enfermagem: "Anotação de Enfermagem",
  banho: "Registrar Banho", diurese: "Registrar Diurese", alimentacao: "Registrar Alimentação",
};
const TIPOS_BANHO = ["Aspersão (chuveiro)", "Imersão (banheira)", "Ablução (parcial)", "No leito"];

const banhoTipoCurto = (t) => (t || "").split(" (")[0];
const banhoLabel = (iso) => {
  if (!iso) return "";
  const [dpart, tpart] = String(iso).split("T");
  const hora = (tpart || "").slice(0, 5);
  const hoje = new Date().toISOString().slice(0, 10);
  const ontem = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dpart === hoje) return `hoje ${hora}`;
  if (dpart === ontem) return `ontem ${hora}`;
  const [y, m, d] = dpart.split("-");
  return `${d}/${m} ${hora}`;
};

const PainelAssistencial = ({ modo = "painel" }) => {
  const {
    residentes, getResidente, auth,
    addRegistroProntuario, refresh, alertas, isAdmin, salvarResidente, gerarAnotacaoInteligente, podeAcao, podeVer, baixarRelatorio,
  } = useApp();
  const podeGerenciarResidentes = isAdmin || podeAcao("residentes", "editar") || podeAcao("residentes", "criar");
  const podeCriarResidente = isAdmin || podeAcao("residentes", "criar");
  const { toast } = useToast();
  const [quick, setQuick] = useState({ open: false, rid: null, tipo: null });
  const [form, setForm] = useState({});
  const [medModal, setMedModal] = useState({ open: false, rid: null });
  const [medEstado, setMedEstado] = useState({});
  const [q, setQ] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("ativos");
  const [novoOpen, setNovoOpen] = useState(false);
  const [snooze, setSnooze] = useState({});
  const [manualRid, setManualRid] = useState(null);
  const [montando, setMontando] = useState(false);
  const [, setTick] = useState(0);
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Ticker: reavalia alarmes de decúbito ao longo do tempo
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  // Permissão de notificação (para avisar mesmo com a aba em segundo plano)
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (e) { /* noop */ }
    }
  }, []);

  const stamp = ([auth?.nome, auth?.funcao].filter(Boolean).join(" — ")) +
    ((auth?.coren && auth?.perfil !== "cuidador") ? ` · ${auth.coren}` : "");

  const abrirQuick = (e, rid, tipo) => {
    e.preventDefault(); e.stopPropagation();
    setForm(tipo === "balanco_hidrico" ? { via: "Ingesta", tipo: "Água", volume: "" }
      : tipo === "evacuacao" ? { quantidade: "Média", aspecto: "normal", obs: "" }
      : tipo === "ocorrencia" ? { gravidade: "Leve", texto: "" }
      : tipo === "anotacao_enfermagem" ? { texto: "", dataHora: agoraISO() }
      : tipo === "diurese" ? { desprezada: true, obs: "", dataHora: agoraISO() }
      : tipo === "alimentacao" ? { refeicao: "Almoço", aceitacao: "Total", tipo: "Sólido", obs: "", dataHora: agoraISO() }
      : tipo === "banho" ? { tipo: TIPOS_BANHO[0], obs: "" }
      : { pa: "", fc: "", fr: "", temp: "", spo2: "", glicemia: "", dor: "", peso: "" });
    setQuick({ open: true, rid, tipo });
  };

  const salvarQuick = async () => {
    const { rid, tipo } = quick;
    if (tipo === "balanco_hidrico" && !form.volume) { toast({ title: "Informe o volume (mL)" }); return; }
    if ((tipo === "ocorrencia" || tipo === "anotacao_enfermagem") && !form.texto?.trim()) { toast({ title: "Descreva o registro" }); return; }
    const { dataHora, ...dados } = form;
    await addRegistroProntuario(rid, { tipo, data: dataHora || agoraISO(), autor: stamp || "Equipe de Enfermagem", dados });
    toast({ title: "Registro salvo", description: TIPO_TITULO[tipo] });
    setQuick({ open: false, rid: null, tipo: null });
    refresh();
  };

  const abrirMed = (e, rid) => {
    e.preventDefault(); e.stopPropagation();
    const al = alertas?.[rid] || {};
    const tratadas = al.medicacoesTratadas || [];
    const estado = {};
    (al.horariosMedicacao || []).forEach((h) => {
      estado[h] = { status: tratadas.includes(h) ? "administrada" : "", motivo: "" };
    });
    setMedEstado(estado);
    setMedModal({ open: true, rid });
  };
  const salvarMed = async () => {
    const { rid } = medModal;
    const entradas = Object.entries(medEstado).filter(([, v]) => v.status && v.status !== "administrada_prev");
    for (const [horario, v] of entradas) {
      if (v.status === "nao_administrada" && !v.motivo?.trim()) { toast({ title: `Informe o motivo de ${horario}` }); return; }
    }
    for (const [horario, v] of entradas) {
      if (v.novo) {
        await addRegistroProntuario(rid, {
          tipo: "medicacao_adm", data: agoraISO(), autor: stamp || "Equipe de Enfermagem",
          dados: { horario, status: v.status, motivo: v.status === "nao_administrada" ? v.motivo : "" },
        });
      }
    }
    toast({ title: "Medicação atualizada" });
    setMedModal({ open: false, rid: null });
    refresh();
  };
  const setMedItem = (h, patch) => setMedEstado((e) => ({ ...e, [h]: { ...e[h], ...patch, novo: true } }));

  const iniciais = (nome) => nome.split(" ").map((n) => n[0]).slice(0, 2).join("");

  const lista = useMemo(() => {
    const arr = residentes
      .filter((r) => {
        const ativo = (r.status || "Ativo") === "Ativo";
        // Painel assistencial e perfis sem gestão de residentes só veem residentes ativos
        if (modo !== "residentes" || !podeGerenciarResidentes) return ativo;
        if (filtroStatus === "ativos") return ativo;
        if (filtroStatus === "inativos") return !ativo;
        return true; // todos
      })
      .filter((r) => modo !== "residentes" || r.nome.toLowerCase().includes(q.toLowerCase()) || (r.quarto || "").includes(q))
      .map((r) => {
        const al = alertas?.[r.id] || {};
        const hid = statusHidratacao(al);
        const med = medicacaoNaHora(al);
        const decubito = statusDecubito(al);
        const prioridade = (decubito.ativo && decubito.due ? 4 : 0) + (hid.atrasada ? 2 : 0) + (med.naHora ? 1 : 0);
        return { r, al, hid, med, decubito, prioridade };
      });
    arr.sort((a, b) => a.r.nome.localeCompare(b.r.nome, "pt-BR"));
    return arr;
  }, [residentes, alertas, q, modo, filtroStatus, podeGerenciarResidentes]);

  const emAtraso = lista.filter((x) => x.prioridade > 0).length;

  // Alarme de decúbito: residentes com prescrição, vencidos e não silenciados
  const decubitoDue = lista.filter(
    (x) => x.decubito?.ativo && x.decubito?.due && (!snooze[x.r.id] || Date.now() > snooze[x.r.id])
  );
  const mkItem = (x) => x && ({ rid: x.r.id, nome: x.r.nome, quarto: x.r.quarto, proxima: x.decubito.proxima, texto: x.decubito.texto });
  const manualItem = manualRid ? lista.find((x) => x.r.id === manualRid) : null;
  // Alarme de decúbito só para Cuidador(a) e Técnico/Auxiliar de Enfermagem
  const podeDecubito = ["cuidador", "tecnico_enfermagem"].includes(auth?.perfil);
  const alarmItem = !podeDecubito ? null : (decubitoDue[0] ? mkItem(decubitoDue[0]) : (manualItem ? mkItem(manualItem) : null));
  const alarmSoundOn = podeDecubito && decubitoDue.length > 0;

  const confirmarDecubito = async (rid, posicao) => {
    await addRegistroProntuario(rid, { tipo: "mudanca_decubito", data: agoraISO(), autor: stamp || "Equipe de Enfermagem", dados: { posicao } });
    setSnooze((sn) => { const n = { ...sn }; delete n[rid]; return n; });
    setManualRid(null);
    toast({ title: "Decúbito registrado", description: posicao });
    refresh();
  };
  const fecharDecubito = (rid) => {
    if (decubitoDue.some((x) => x.r.id === rid)) {
      setSnooze((sn) => ({ ...sn, [rid]: Date.now() + 15 * 60 * 1000 }));
    }
    setManualRid(null);
  };

  const montarAnotacao = async () => {
    if (!quick.rid) return;
    setMontando(true);
    try {
      const res = await gerarAnotacaoInteligente(quick.rid);
      setForm((f) => ({ ...f, texto: res.texto || "" }));
      toast({ title: `Rascunho do plantão ${res.turno || ""}`, description: "Revise antes de salvar." });
    } catch (e) {
      toast({ title: "Não foi possível montar", description: e?.response?.data?.detail || "Sem cuidados no plantão atual." });
    } finally { setMontando(false); }
  };

  return (
    <div className="space-y-6" data-testid={modo === "residentes" ? "residentes-page" : "painel-assistencial"}>
      <AvisoAlertaTopo />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {modo === "residentes" ? "Residentes" : `Olá, ${auth?.nome?.split(" ")[0] || "Equipe"}`}
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Cuidados dos residentes — idade, dieta, alergias, hidratação, evacuação e medicação
            {emAtraso > 0 && <span className="ml-1 text-red-600 font-medium">· {emAtraso} em atenção</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {podeVer("tv") !== false && <Link to="/painel-tv" data-testid="modo-tv-btn">
            <Button variant="outline" className="gap-2 border-slate-800 bg-slate-900 text-white hover:bg-slate-800 hover:text-white">
              <Monitor className="w-4 h-4" /> Modo TV
            </Button>
          </Link>}
          {modo === "residentes" && (isAdmin || podeVer("relatorios")) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 border-slate-300 text-slate-700" data-testid="relatorio-btn">
                  <Printer className="w-4 h-4" /> Relatório
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem className="gap-2 cursor-pointer" data-testid="relatorio-residentes-item"
                  onClick={() => baixarRelatorio("residentes-ativos").catch(() => toast({ title: "Falha ao gerar o relatório" }))}>
                  <Users className="w-4 h-4 text-blue-600" /> Relatório de Residentes (Ativos)
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer" data-testid="relatorio-ocupacao-item"
                  onClick={() => baixarRelatorio("ocupacao").catch(() => toast({ title: "Falha ao gerar o relatório" }))}>
                  <BedDouble className="w-4 h-4 text-emerald-600" /> Relatório de Ocupação
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer" data-testid="relatorio-aniversariantes-item"
                  onClick={() => baixarRelatorio("aniversariantes").catch(() => toast({ title: "Falha ao gerar o relatório" }))}>
                  <Cake className="w-4 h-4 text-pink-600" /> Aniversariantes do Mês
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {modo === "residentes" && podeCriarResidente && (
            <Button onClick={() => setNovoOpen(true)} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="novo-residente-btn">
              <UserPlus className="w-4 h-4" /> Novo Residente
            </Button>
          )}
        </div>
      </div>

      {modo === "residentes" && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 max-w-sm flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou quarto..." className="flex-1 bg-transparent outline-none text-sm text-slate-700" />
          </div>
          {podeGerenciarResidentes && (
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="filtro-status-residente">
              {[["ativos", "Ativos"], ["inativos", "Inativos"], ["todos", "Todos"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setFiltroStatus(val)}
                  data-testid={`filtro-status-${val}`}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filtroStatus === val ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cards de residentes */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {lista.map(({ r, al, hid, med, decubito }) => {
          const g = grauBadge[r.grauDependencia];
          const idade = calcularIdade(r.dataNascimento);
          const evac = statusEvacuacao(al);
          return (
            <div key={r.id} data-testid={`residente-card-${r.id}`}
              className={`bg-white rounded-xl p-5 shadow-sm transition-all border ${hid.atrasada ? "border-red-400 ring-1 ring-red-200" : "border-slate-100"}`}>
              <div className="flex items-center gap-3">
                {r.foto ? <img src={r.foto} alt={r.nome} className="w-12 h-12 rounded-xl object-cover" />
                  : <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center font-semibold">{iniciais(r.nome)}</div>}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                    {r.nome}
                    {(r.status || "Ativo") !== "Ativo" && (
                      <span className="inline-flex items-center text-[10px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded shrink-0" data-testid={`card-inativo-${r.id}`}>Inativo</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                    <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> Quarto {r.quarto || "-"}</span>
                    {idade != null && <span className="flex items-center gap-1"><Cake className="w-3.5 h-3.5" /> {idade} anos</span>}
                  </p>
                </div>
                <HydrationDonut pct={hid.pct} total={hid.total} atingida={hid.atingida} />
              </div>

              <div className="mt-3 space-y-1.5 text-xs">
                <p className="flex items-start gap-1.5 text-slate-600"><Utensils className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" /> <span><span className="text-slate-400">Dieta:</span> {r.dieta || "—"}</span></p>
                <p className="flex items-start gap-1.5 text-slate-600"><TriangleAlert className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" /> <span><span className="text-slate-400">Alergias:</span> {r.alergias || "—"}</span></p>
                {decubito.ativo && (
                  <p className="flex items-start gap-1.5 text-indigo-700" data-testid={`decubito-orientacao-${r.id}`}><RotateCcw className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" /> <span><span className="text-indigo-400">Decúbito:</span> {decubito.cuidado}{decubito.frequencia ? ` · ${decubito.frequencia}` : ""}{decubito.orientacao ? ` — ${decubito.orientacao}` : ""}</span></p>
                )}
                {g && <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded ${g.cls}`}>{g.label}</span>}
              </div>

              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <span data-testid={`hidratacao-${r.id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md ${hid.atingida ? "text-emerald-700 bg-emerald-50" : hid.atrasada ? "text-red-700 bg-red-50" : "text-sky-700 bg-sky-50"}`}>
                  <Droplets className="w-3.5 h-3.5" /> {hid.atingida ? "Meta 1,6L ✓" : hid.texto}{hid.atrasada ? " · +1h!" : ""}
                </span>
                <span data-testid={`evacuacao-${r.id}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${evac.alerta ? "font-semibold text-red-700 bg-red-50 border border-red-200" : "font-medium text-slate-600 bg-slate-100"}`}>
                  Evacuação: {evac.texto}{evac.desc ? ` (${evac.desc})` : ""}
                  {evac.dias != null && evac.dias >= 1 && <span data-testid={`evacuacao-dias-${r.id}`}> · {evac.dias} {evac.dias === 1 ? "dia" : "dias"} sem evacuar</span>}
                </span>
                {med.naHora ? (
                  <span data-testid={`medicacao-alerta-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md text-amber-700 bg-amber-50 animate-pulse">
                    <Pill className="w-3.5 h-3.5" /> Medicação {med.horario}
                  </span>
                ) : med.proxima ? (
                  <span data-testid={`medicacao-proxima-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md text-slate-600 bg-slate-100">
                    <Pill className="w-3.5 h-3.5" /> Próxima medicação {med.proxima}
                  </span>
                ) : null}
                {al?.ultimaAlimentacao && (
                  <span data-testid={`alimentacao-${r.id}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${al.negouSeguidas >= 2 ? "font-semibold text-red-700 bg-red-50 border border-red-200" : /negou/i.test(al.ultimaAlimentacao.aceitacao || "") ? "font-medium text-amber-700 bg-amber-50" : "font-medium text-lime-700 bg-lime-50"}`}>
                    <Utensils className="w-3.5 h-3.5" /> {al.ultimaAlimentacao.refeicao} {banhoLabel(al.ultimaAlimentacao.data)} · {al.ultimaAlimentacao.aceitacao}
                    {al.negouSeguidas >= 2 && <span data-testid={`alimentacao-alerta-${r.id}`}> · negou {al.negouSeguidas} refeições seguidas</span>}
                  </span>
                )}
                {(() => {
                  const ult = al?.ultimaDiurese ? new Date(al.ultimaDiurese) : null;
                  const horas = ult ? (Date.now() - ult.getTime()) / 3600000 : null;
                  const alerta = horas != null && horas > 8;
                  return (
                    <span data-testid={`diurese-${r.id}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${alerta ? "font-semibold text-red-700 bg-red-50 border border-red-200" : ult ? "font-medium text-yellow-700 bg-yellow-50" : "font-medium text-slate-400 bg-slate-50"}`}>
                      <Droplets className="w-3.5 h-3.5" />
                      {ult ? <>Diurese {banhoLabel(al.ultimaDiurese)}{alerta && <span data-testid={`diurese-alerta-${r.id}`}> · {Math.floor(horas)}h sem diurese</span>}</> : "Sem diurese registrada"}
                    </span>
                  );
                })()}
                {al?.ultimoBanho ? (
                  <span data-testid={`banho-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md text-cyan-700 bg-cyan-50">
                    <Droplets className="w-3.5 h-3.5" /> Banho {banhoLabel(al.ultimoBanho)}{al.ultimoBanhoTipo ? ` · ${banhoTipoCurto(al.ultimoBanhoTipo)}` : ""}
                  </span>
                ) : (
                  <span data-testid={`banho-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md text-slate-400 bg-slate-50">
                    <Droplets className="w-3.5 h-3.5" /> Sem banho registrado
                  </span>
                )}
                {decubito.ativo && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setManualRid(r.id); }}
                    data-testid={`decubito-badge-${r.id}`}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md transition-colors ${decubito.due ? "text-red-700 bg-red-50 animate-pulse" : "text-indigo-700 bg-indigo-50 hover:bg-indigo-100"}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> {decubito.due
                      ? "Decúbito: mudar agora!"
                      : `Decúbito ${decubito.proximoHorario ? decubito.proximoHorario.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}`}
                  </button>
                )}
              </div>

              {/* Ações rápidas */}
              <div className="grid grid-cols-3 gap-2 mt-3">
                <button onClick={(e) => abrirQuick(e, r.id, "sinais_vitais")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg py-2 transition-colors" data-testid={`acao-sinais-${r.id}`}><Activity className="w-3.5 h-3.5" /> Sinais</button>
                <button onClick={(e) => abrirQuick(e, r.id, "balanco_hidrico")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 rounded-lg py-2 transition-colors" data-testid={`acao-hidratacao-${r.id}`}><Droplets className="w-3.5 h-3.5" /> Hidratar</button>
                <button onClick={(e) => abrirQuick(e, r.id, "evacuacao")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg py-2 transition-colors" data-testid={`acao-evacuacao-${r.id}`}><ClipboardList className="w-3.5 h-3.5" /> Evacuação</button>
                <button onClick={(e) => abrirMed(e, r.id)} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg py-2 transition-colors" data-testid={`acao-medicacao-${r.id}`}><Pill className="w-3.5 h-3.5" /> Medicação</button>
                <button onClick={(e) => abrirQuick(e, r.id, "banho")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg py-2 transition-colors" data-testid={`acao-banho-${r.id}`}><Droplets className="w-3.5 h-3.5" /> Banho</button>
                <button onClick={(e) => abrirQuick(e, r.id, "ocorrencia")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg py-2 transition-colors" data-testid={`acao-ocorrencia-${r.id}`}><TriangleAlert className="w-3.5 h-3.5" /> Ocorrência</button>
                <button onClick={(e) => abrirQuick(e, r.id, "anotacao_enfermagem")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg py-2 transition-colors" data-testid={`acao-anotacao-${r.id}`}><ClipboardList className="w-3.5 h-3.5" /> Anotação</button>
                <button onClick={(e) => abrirQuick(e, r.id, "alimentacao")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-lime-700 bg-lime-50 hover:bg-lime-100 rounded-lg py-2 transition-colors" data-testid={`acao-alimentacao-${r.id}`}><Utensils className="w-3.5 h-3.5" /> Alimentação</button>
                <button onClick={(e) => abrirQuick(e, r.id, "diurese")} className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 rounded-lg py-2 transition-colors" data-testid={`acao-diurese-${r.id}`}><Droplets className="w-3.5 h-3.5" /> Diurese</button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Link to={`/residentes/${r.id}/prontuario`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg py-2 transition-colors" data-testid={`painel-prontuario-${r.id}`}><Stethoscope className="w-3.5 h-3.5" /> Prontuário</Link>
                <Link to={`/residentes/${r.id}`} className="inline-flex items-center justify-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg py-2 px-3 transition-colors">Ficha <ChevronRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de registro rápido */}
      <Dialog open={quick.open} onOpenChange={(o) => setQuick((x) => ({ ...x, open: o }))}>
        <DialogContent aria-describedby={undefined} data-testid="quick-record-modal">
          <DialogHeader><DialogTitle>{TIPO_TITULO[quick.tipo] || "Registro"}{quick.rid ? ` — ${getResidente(quick.rid)?.nome || ""}` : ""}</DialogTitle></DialogHeader>
          {quick.tipo === "sinais_vitais" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-500">PA (ex.: 12/8)</label><input className={inputCls} data-testid="quick-pa" value={form.pa || ""} onChange={(e) => s("pa", e.target.value)} placeholder="12/8" /></div>
              <div><label className="text-xs font-medium text-slate-500">FC (bpm)</label><input className={inputCls} value={form.fc || ""} onChange={(e) => s("fc", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">FR (irpm)</label><input className={inputCls} value={form.fr || ""} onChange={(e) => s("fr", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Temp (°C)</label><input className={inputCls} value={form.temp || ""} onChange={(e) => s("temp", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">SpO2 (%)</label><input className={inputCls} value={form.spo2 || ""} onChange={(e) => s("spo2", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Glicemia (mg/dL)</label><input className={inputCls} data-testid="quick-glicemia" value={form.glicemia || ""} onChange={(e) => s("glicemia", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Dor (0-10)</label><input className={inputCls} value={form.dor || ""} onChange={(e) => s("dor", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Peso (kg)</label><input className={inputCls} value={form.peso || ""} onChange={(e) => s("peso", e.target.value)} /></div>
            </div>
          )}
          {quick.tipo === "balanco_hidrico" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-500">Descrição</label><input className={inputCls} value={form.tipo || ""} onChange={(e) => s("tipo", e.target.value)} placeholder="Água, Suco..." /></div>
              <div><label className="text-xs font-medium text-slate-500">Volume (mL)</label><input type="number" className={inputCls} data-testid="quick-volume" value={form.volume || ""} onChange={(e) => s("volume", e.target.value)} /></div>
            </div>
          )}
          {quick.tipo === "evacuacao" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-500">Quantidade</label>
                <Select value={form.quantidade} onValueChange={(v) => s("quantidade", v)}>
                  <SelectTrigger data-testid="evac-quantidade"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Pouca", "Média", "Muita"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs font-medium text-slate-500">Aspecto</label>
                <Select value={form.aspecto} onValueChange={(v) => s("aspecto", v)}>
                  <SelectTrigger data-testid="evac-aspecto"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASPECTOS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><label className="text-xs font-medium text-slate-500">Observação</label><input className={inputCls} value={form.obs || ""} onChange={(e) => s("obs", e.target.value)} /></div>
            </div>
          )}
          {quick.tipo === "ocorrencia" && (
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-slate-500">Gravidade</label>
                <Select value={form.gravidade} onValueChange={(v) => s("gravidade", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Leve", "Moderada", "Grave"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs font-medium text-slate-500">Descrição</label><textarea rows={3} className={inputCls} data-testid="quick-texto" value={form.texto || ""} onChange={(e) => s("texto", e.target.value)} /></div>
            </div>
          )}
          {quick.tipo === "anotacao_enfermagem" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-500">Anotação cronológica — pode ser registrada a qualquer horário.</p>
                <Button type="button" variant="outline" className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={montarAnotacao} disabled={montando} data-testid="quick-montar-anotacao">
                  <ClipboardList className="w-3.5 h-3.5" /> {montando ? "Montando..." : "Montar da linha do tempo"}
                </Button>
              </div>
              <div><label className="text-xs font-medium text-slate-500">Data e hora</label><input type="datetime-local" className={inputCls} data-testid="quick-anotacao-data" value={form.dataHora || ""} onChange={(e) => s("dataHora", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Anotação</label><textarea rows={6} className={inputCls} data-testid="quick-texto" value={form.texto || ""} onChange={(e) => s("texto", e.target.value)} placeholder="Aceitação de dieta, higiene, eliminações, humor, intercorrências do plantão..." /></div>
            </div>
          )}
          {quick.tipo === "alimentacao" && (
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-slate-500">Refeição</label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">{["Café", "Lanche", "Almoço", "Café da tarde", "Jantar", "Ceia"].map((o) => (
                  <button type="button" key={o} onClick={() => s("refeicao", o)} data-testid={`alim-refeicao-${o}`} className={`text-xs px-2 py-1.5 rounded-md border ${form.refeicao === o ? "bg-lime-600 text-white border-lime-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{o}</button>))}</div></div>
              <div><label className="text-xs font-medium text-slate-500">Aceitação</label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">{["Total", "Parcial", "Negou refeição"].map((o) => (
                  <button type="button" key={o} onClick={() => s("aceitacao", o)} data-testid={`alim-aceitacao-${o}`} className={`text-xs px-2 py-1.5 rounded-md border ${form.aceitacao === o ? (o === "Negou refeição" ? "bg-red-600 border-red-600" : "bg-lime-600 border-lime-600") + " text-white" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{o}</button>))}</div></div>
              <div><label className="text-xs font-medium text-slate-500">Tipo</label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">{["Sólido", "Líquido", "Pastoso"].map((o) => (
                  <button type="button" key={o} onClick={() => s("tipo", o)} data-testid={`alim-tipo-${o}`} className={`text-xs px-2 py-1.5 rounded-md border ${form.tipo === o ? "bg-lime-600 text-white border-lime-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{o}</button>))}</div></div>
              <div><label className="text-xs font-medium text-slate-500">Data e hora</label><input type="datetime-local" className={inputCls} data-testid="quick-alimentacao-data" value={form.dataHora || ""} onChange={(e) => s("dataHora", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Observações (opcional)</label><input className={inputCls} data-testid="quick-alimentacao-obs" value={form.obs || ""} onChange={(e) => s("obs", e.target.value)} placeholder="Ex.: comeu metade, engasgo, recusou líquidos..." /></div>
            </div>
          )}
          {quick.tipo === "diurese" && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={form.desprezada !== false} onChange={(e) => s("desprezada", e.target.checked)} data-testid="quick-diurese-desprezada" />
                <span><b>Diurese desprezada</b> — o residente urinou e a diurese foi desprezada.</span>
              </label>
              <div><label className="text-xs font-medium text-slate-500">Data e hora</label><input type="datetime-local" className={inputCls} data-testid="quick-diurese-data" value={form.dataHora || ""} onChange={(e) => s("dataHora", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-slate-500">Observações (opcional)</label><input className={inputCls} data-testid="quick-diurese-obs" value={form.obs || ""} onChange={(e) => s("obs", e.target.value)} placeholder="Coloração, odor, volume aproximado..." /></div>
            </div>
          )}
          {quick.tipo === "banho" && (
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-slate-500">Tipo de banho</label>
                <select className={inputCls} data-testid="quick-banho-tipo" value={form.tipo || TIPOS_BANHO[0]} onChange={(e) => s("tipo", e.target.value)}>
                  {TIPOS_BANHO.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium text-slate-500">Observações (opcional)</label><textarea rows={2} className={inputCls} data-testid="quick-banho-obs" value={form.obs || ""} onChange={(e) => s("obs", e.target.value)} placeholder="Higiene, aceitação, condições da pele..." /></div>
            </div>
          )}
          <p className="text-[11px] text-slate-400">Registrando como: {stamp || "Equipe de Enfermagem"}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuick({ open: false, rid: null, tipo: null })}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvarQuick} data-testid="quick-salvar"><Save className="w-4 h-4" /> Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação de medicação */}
      <Dialog open={medModal.open} onOpenChange={(o) => setMedModal((x) => ({ ...x, open: o }))}>
        <DialogContent aria-describedby={undefined} data-testid="med-confirm-modal" className="max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>Confirmar Medicação{medModal.rid ? ` — ${getResidente(medModal.rid)?.nome || ""}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 pr-1 -mr-1" data-testid="med-confirm-scroll">
            {Object.keys(medEstado).length === 0 && <p className="text-sm text-slate-400">Nenhum horário de medicação cadastrado para este residente.</p>}
            {Object.entries(medEstado).map(([h, v]) => (
              <div key={h} className="border border-slate-100 rounded-lg p-3" data-testid={`med-item-${h}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700 flex items-center gap-1.5"><Pill className="w-4 h-4 text-amber-600" /> {h}</span>
                  {v.status === "administrada" && !v.novo && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Já administrada</span>}
                </div>
                {!(v.status === "administrada" && !v.novo) && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button onClick={() => setMedItem(h, { status: "administrada", motivo: "" })} className={`text-xs px-3 py-1.5 rounded-lg ${v.status === "administrada" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`} data-testid={`med-adm-${h}`}>Administrada</button>
                    <button onClick={() => setMedItem(h, { status: "nao_administrada" })} className={`text-xs px-3 py-1.5 rounded-lg ${v.status === "nao_administrada" ? "bg-red-600 text-white" : "bg-red-50 text-red-700"}`} data-testid={`med-nao-${h}`}>Não administrada</button>
                    {v.status === "nao_administrada" && (
                      <input className={`${inputCls} mt-1`} placeholder="Motivo da pendência" value={v.motivo || ""} onChange={(e) => setMedItem(h, { motivo: e.target.value })} data-testid={`med-motivo-${h}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMedModal({ open: false, rid: null })}>Fechar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvarMed} data-testid="med-salvar"><Save className="w-4 h-4" /> Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {modo === "residentes" && <ResidenteFormDialog open={novoOpen} onOpenChange={setNovoOpen} onSave={salvarResidente} />}

      <DecubitoAlarm item={alarmItem} soundOn={alarmSoundOn} onConfirm={confirmarDecubito} onSnooze={fecharDecubito} />
    </div>
  );
};

export default PainelAssistencial;
